/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";

import { SearchIcon } from "../../../../emotion";
import type { ThemeType } from "../../../../global/emotion";
import {
  fontSizes,
  fontWeights,
  radius,
  spacing,
} from "../../../../global/emotion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./dialog.emotion";

// Styles
const commandStyle = (theme: ThemeType) => ({
  backgroundColor: theme.semanticColors.background,
  color: theme.semanticColors.primary,
  display: "flex",
  height: "100%",
  width: "100%",
  flexDirection: "column" as const,
  overflow: "hidden",
  borderRadius: radius.ms,
});

const inputWrapperStyle = (theme: ThemeType) => ({
  display: "flex",
  height: spacing.xl,
  alignItems: "center",
  gap: spacing.xs,
  borderBottom: `1px solid ${theme.semanticColors.neutralsLower}`,
  paddingInline: spacing.ms,
});

const inputIconStyle = (theme: ThemeType) => ({
  width: "16px",
  height: "16px",
  flexShrink: 0,
  opacity: 0.5,
  color: theme.semanticColors.neutralsMedium,
});

const inputStyle = (theme: ThemeType) => ({
  display: "flex",
  height: spacing.l,
  width: "100%",
  borderRadius: radius.ms,
  backgroundColor: "transparent",
  paddingBlock: spacing.ms,
  fontSize: fontSizes.bodyM,
  outline: "none",
  border: "none",
  color: theme.semanticColors.primary,

  "&::placeholder": {
    color: theme.semanticColors.neutralsMedium,
  },

  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
});

const listStyle = {
  maxHeight: "300px",
  scrollPaddingBlock: "4px",
  overflowX: "hidden" as const,
  overflowY: "auto" as const,
};

const emptyStyle = (theme: ThemeType) => ({
  paddingBlock: spacing.m,
  textAlign: "center" as const,
  fontSize: fontSizes.bodyM,
  color: theme.semanticColors.neutralsMedium,
});

const groupStyle = (theme: ThemeType) => ({
  color: theme.semanticColors.primary,
  overflow: "hidden",
  padding: spacing.xxs,

  "& [cmdk-group-heading]": {
    color: theme.semanticColors.neutralsMedium,
    paddingInline: spacing.xs,
    paddingBlock: "6px",
    fontSize: fontSizes.detailL,
    fontWeight: fontWeights.detailL,
  },
});

const separatorStyle = (theme: ThemeType) => ({
  backgroundColor: theme.semanticColors.neutralsLower,
  marginInline: `-${spacing.xxs}`,
  height: "1px",
});

const itemStyle = (theme: ThemeType) => ({
  position: "relative" as const,
  display: "flex",
  cursor: "default",
  alignItems: "center",
  gap: spacing.xs,
  borderRadius: radius.ms,
  paddingInline: spacing.ms,
  paddingBlock: spacing.s,
  fontSize: fontSizes.bodyM,
  outline: "none",
  userSelect: "none" as const,
  color: theme.semanticColors.primary,

  "&:focus-visible": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.neutralsMedium,
  },

  "&:active": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.neutralsMedium,
  },

  "&[data-selected='true']": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.primary,
  },

  "&[data-disabled='true']": {
    pointerEvents: "none" as const,
    opacity: 0.5,
  },

  "& svg": {
    pointerEvents: "none" as const,
    flexShrink: 0,
    width: "16px",
    height: "16px",

    "&:not([class*='text-'])": {
      color: theme.semanticColors.neutralsMedium,
    },
  },
});

const shortcutStyle = (theme: ThemeType) => ({
  marginLeft: "auto",
  fontSize: fontSizes.detailL,
  letterSpacing: "0.1em",
  color: theme.semanticColors.neutralsMedium,
});

// Dialog styles for CommandDialog
const dialogContentStyle = {
  overflow: "hidden",
  padding: 0,
};

const commandDialogStyle = (theme: ThemeType) => ({
  ...commandStyle(theme),

  "& [cmdk-group-heading]": {
    color: theme.semanticColors.neutralsMedium,
    paddingInline: spacing.xs,
    fontWeight: fontWeights.detailL,
  },

  "& [data-slot='command-input-wrapper']": {
    height: spacing.xl,
  },

  "& [cmdk-group]": {
    paddingInline: spacing.xs,

    "&:not([hidden]) ~ [cmdk-group]": {
      paddingTop: 0,
    },
  },

  "& [cmdk-input-wrapper] svg": {
    height: "20px",
    width: "20px",
  },

  "& [cmdk-input]": {
    height: spacing.xl,
  },

  "& [cmdk-item]": {
    paddingInline: spacing.xs,
    paddingBlock: spacing.ms,

    "& svg": {
      height: "20px",
      width: "20px",
    },
  },
});

const srOnlyStyle = {
  position: "absolute" as const,
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap" as const,
  borderWidth: 0,
};

function Command({ ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return <CommandPrimitive data-slot="command" css={commandStyle} {...props} />;
}

function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  showCloseButton?: boolean;
}) {
  return (
    <Dialog {...props}>
      <DialogHeader css={srOnlyStyle}>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent css={dialogContentStyle} showCloseButton={showCloseButton}>
        <Command css={commandDialogStyle}>{children}</Command>
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  wrapperCss,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input> & {
  wrapperCss?: Interpolation<ThemeType>;
}) {
  return (
    <div
      data-slot="command-input-wrapper"
      css={[inputWrapperStyle, wrapperCss]}
    >
      <SearchIcon css={inputIconStyle} />
      <CommandPrimitive.Input
        data-slot="command-input"
        css={[inputStyle]}
        {...props}
      />
    </div>
  );
}

function CommandList({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      css={listStyle}
      {...props}
    />
  );
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      css={emptyStyle}
      {...props}
    />
  );
}

function CommandGroup({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      css={groupStyle}
      {...props}
    />
  );
}

function CommandSeparator({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      css={separatorStyle}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      css={itemStyle}
      className={className}
      {...props}
    />
  );
}

function CommandShortcut({ ...props }: React.ComponentProps<"span">) {
  return <span data-slot="command-shortcut" css={shortcutStyle} {...props} />;
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
